import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { getScenarioStatus } from "../../views/Cockpit/viewServices/calls/apiCalls";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import AuthContext from "../../connections/auth/AuthProvider.js";

/**
 * Simplified scenario data hook - single source of truth with simple polling
 */
export const useScenarioData = () => {
  const { t } = useTranslation("translation", { keyPrefix: "rgm.cockpit" });
  const { user, authTokens } = useContext(AuthContext);

  // Single state for all scenarios
  const [allScenarios, setAllScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastCompletedScenarioRef = useRef(null);

  // Single fetch function for all scenarios
  const fetchScenarios = useCallback(
    async (showErrorToast = true) => {
      // Don't fetch if user isn't properly authenticated
      if (!user || !authTokens?.access) {
        console.warn("⚠️ User not authenticated, skipping scenario fetch");
        return [];
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log("🔍 Fetching scenarios from API...");
        const scenarios = await getScenarioStatus();
        console.log("✅ Received", scenarios.length, "scenarios");

        setAllScenarios((prevScenarios) => {
          // Check if any scenario just completed (for notifications)
          const previousLatest = prevScenarios[0];
          const currentLatest = scenarios[0];

          if (
            previousLatest &&
            currentLatest &&
            previousLatest.scenario_id === currentLatest.scenario_id &&
            ["QUEUED", "PROCESSING", "STARTED"].includes(
              previousLatest.status,
            ) &&
            ["FINISHED", "ERROR"].includes(currentLatest.status)
          ) {
            console.log("🎉 Scenario completed:", currentLatest.scenario_name);
            lastCompletedScenarioRef.current = currentLatest;
          }

          return scenarios;
        });

        setIsInitialized(true);
        return scenarios;
      } catch (err) {
        console.error("❌ Failed to fetch scenarios:", err);
        setError(err);

        // Only show toast after initial load succeeds at least once
        if (isInitialized && showErrorToast) {
          toast.error(t("toast.load_error"));
        } else if (!isInitialized) {
          console.warn("⚠️ Initial scenario fetch failed, will retry silently");
        }

        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [t, isInitialized, user, authTokens],
  );

  // Simple polling logic
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    console.log("▶️ Starting scenario polling...");
    setIsPolling(true);

    const poll = async () => {
      const scenarios = await fetchScenarios(false); // Don't show toast errors during polling

      // Stop polling if no running scenarios
      const hasRunningScenarios = scenarios.some((s) =>
        ["QUEUED", "PROCESSING", "STARTED"].includes(s.status),
      );

      if (!hasRunningScenarios) {
        console.log("⏹️ No running scenarios found, stopping polling");
        setIsPolling(false);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    };

    // Initial poll
    poll();

    // Set up interval
    pollIntervalRef.current = setInterval(poll, 2000);
  }, [fetchScenarios]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      console.log("⏹️ Stopping polling");
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Enable polling (called when user creates new scenario)
  const enablePolling = useCallback(() => {
    console.log("🚀 Enabling polling for new scenario");
    startPolling();
  }, [startPolling]);

  // Refresh data manually
  const refreshData = useCallback(async () => {
    return await fetchScenarios(true); // Show toast errors for manual refresh
  }, [fetchScenarios]);

  // Derived values (computed each render - simple and reliable)
  const latestScenario = allScenarios[0] || null;
  const completedScenarios = allScenarios
    .filter(
      (scenario) => scenario.status === "FINISHED", // Only FINISHED scenarios for selectors
    )
    .map((scenario) => ({
      scenario_id: scenario.scenario_id,
      scenario_name: scenario.scenario_name,
      folder_id: scenario.folder_id,
      is_archived: scenario.is_archived,
      status: scenario.status,
      visibility: scenario.visibility,
    }));

  // Recently completed scenario management
  const getRecentlyCompletedScenario = useCallback(() => {
    return lastCompletedScenarioRef.current;
  }, []);

  const clearRecentlyCompletedScenario = useCallback(() => {
    lastCompletedScenarioRef.current = null;
  }, []);

  // Optimistic update methods for instant UI feedback
  const updateScenarioLocally = useCallback((scenarioId, updates) => {
    setAllScenarios((prev) =>
      prev.map((s) =>
        s.scenario_id === scenarioId ? { ...s, ...updates } : s,
      ),
    );
  }, []);

  const removeScenarioLocally = useCallback((scenarioId) => {
    setAllScenarios((prev) => prev.filter((s) => s.scenario_id !== scenarioId));
  }, []);

  const restoreScenarioLocally = useCallback((scenario) => {
    setAllScenarios((prev) => {
      // Insert in the correct position (by scenario_id or timestamp)
      const newList = [...prev, scenario].sort(
        (a, b) => b.scenario_id - a.scenario_id,
      );
      return newList;
    });
  }, []);

  // Initial data fetch and polling setup
  useEffect(() => {
    console.log("🚀 useScenarioData MOUNTING - Initial data fetch");

    // Single initial fetch that also checks for polling
    const initializeData = async () => {
      // Wait for authentication to be ready
      if (!user || !authTokens?.access) {
        console.log("⏳ Waiting for user authentication to be ready...");
        return;
      }

      const scenarios = await fetchScenarios(false); // Don't show toast on initial load
      const hasRunningScenarios = scenarios.some((s) =>
        ["QUEUED", "PROCESSING", "STARTED"].includes(s.status),
      );
      if (hasRunningScenarios && !isPolling) {
        startPolling();
      }
    };

    initializeData();
  }, [user, authTokens?.access, startPolling, isPolling, fetchScenarios]); // Re-run when authentication changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("🔥 useScenarioData UNMOUNTING - Cleanup");
      stopPolling();
    };
  }, [stopPolling]);

  console.log("🎯 useScenarioData state:", {
    totalScenarios: allScenarios.length,
    completedScenarios: completedScenarios.length,
    latestScenario: latestScenario?.scenario_name,
    isPolling,
  });

  return {
    // Main data - simple and straightforward
    scenarios: completedScenarios, // For selectors (FINISHED scenarios only)
    allScenarios, // All scenarios with status (for details modal - includes ERROR scenarios)
    latestScenario, // For floating status
    isLoading,
    error,
    isPolling,

    // Actions
    refreshData,
    enablePolling,
    stopPolling,

    // Recently completed scenario management
    getRecentlyCompletedScenario,
    clearRecentlyCompletedScenario,

    // Optimistic update methods
    updateScenarioLocally,
    removeScenarioLocally,
    restoreScenarioLocally,
  };
};
